import React, { useState, useEffect } from 'react';

const Disclaimers = () => {
  const [isDarkMode, setIsDarkMode] = useState(
    () => document.body.className === 'darkTheme'
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.className === 'darkTheme');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  /* ── Theme tokens ─────────────────────────────────────────────── */
  const t = {
    pageBg: 'transparent',
    cardBg: isDarkMode ? '#1e2130' : '#ffffff',
    cardBorder: isDarkMode ? '#2e3350' : '#e8e0d0',
    titleColor: isDarkMode ? '#c9a84c' : '#8B6914',
    textColor: isDarkMode ? '#cccccc' : '#3d3d3d',
    badgeBg: isDarkMode ? '#c9a84c22' : '#f5edd8',
    badgeText: isDarkMode ? '#c9a84c' : '#8B6914',
    h3Color: isDarkMode ? '#e8e8e8' : '#1a1a1a',
    divider: isDarkMode ? '#2e3350' : '#e8e0d0',
    noteBg: isDarkMode ? '#1e2130' : '#fffbf0',
    mutedText: isDarkMode ? '#aaaaaa' : '#666666',
    introText: isDarkMode ? '#cccccc' : '#3d3d3d',
  };

  const cardStyle = {
    backgroundColor: t.cardBg,
    border: `1px solid ${t.cardBorder}`,
    borderLeft: '4px solid #c9a84c',
    borderRadius: '10px',
    padding: '1.5rem 1.75rem',
    marginBottom: '1rem',
    transition: 'background-color 0.3s ease, border-color 0.3s ease',
  };

  const sectionHeadingStyle = {
    color: t.titleColor,
    fontWeight: 700,
    borderBottom: `2px solid ${t.divider}`,
    paddingBottom: '0.5rem',
    marginBottom: '1.5rem',
    marginTop: '2rem',
    fontSize: '1.15rem',
    transition: 'color 0.3s ease',
  };

  const bulletListStyle = {
    paddingLeft: '1.5rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    color: t.textColor,
  };

  return (
    <div className="main-contain pt-5 mt-5 mb-5" style={{ textAlign: 'justify' }}>
      <section>

        {/* ── Page Header ── */}
        <div className="iq-banner-2">
          <div className="banner-info">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-11">
                  <div className="banner-text iq-font-white wow fadeIn" data-wow-delay="0.1s">
                    <h2 className="h2 text-primary">CGO Token Legal Disclaimer &amp; Risk Disclosure</h2>
                    <p style={{ color: t.mutedText, fontSize: '1rem', marginTop: '0.4rem' }}>
                      Important information about risks associated with CGO Tokens
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="hero-internal-separator mt-5"></div>
        </div>

        {/* ── Content ── */}
        <section className="iq-product-description overview-block-pt pt-0">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-11">

                {/* ── General Disclaimer ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    General Disclaimer
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    The information contained on this website is provided for general informational purposes only. Nothing on this website constitutes financial advice, investment advice, legal advice, or a recommendation to purchase, sell, or hold any digital asset. Users should conduct their own independent research and consult professional advisors before making any financial or investment decisions.
                  </p>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    CGO Tokens are digital tokens designed to represent the value of physical gold held in secure vault facilities. Ownership of CGO Tokens does not constitute ownership of shares in the issuing company, nor does it grant any equity, voting rights, or ownership interest in any corporate entity.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    The issuer makes no guarantees regarding future market performance, liquidity, or price stability of CGO Tokens.
                  </p>
                </div>

                {/* ── Gold Backing Disclosure ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Gold Backing Disclosure
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    Each CGO Token is designed to represent the value equivalent to one gram of physical gold held in secured vault facilities operated by professional custodians. The gold reserves supporting CGO Tokens are stored according to industry-standard security and custody practices.
                  </p>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    While the issuer endeavors to maintain a 1:1 relationship between CGO Tokens and the underlying gold reserves, users acknowledge that operational, technological, or regulatory circumstances may affect redemption processes or timing.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Gold prices fluctuate due to global market conditions, and the value of CGO Tokens may change accordingly.
                  </p>
                </div>

                {/* ── Digital Asset Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Digital Asset Risk
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    Digital assets involve a high degree of risk. Users should carefully consider the risks associated with blockchain-based tokens before acquiring CGO Tokens.
                  </p>
                  <p className="fs-18 mb-1" style={{ color: t.textColor }}>Risks include, but are not limited to:</p>
                  <ul style={bulletListStyle} className="fs-18">
                    <li>blockchain network failures</li>
                    <li>smart contract vulnerabilities</li>
                    <li>cyber security incidents</li>
                    <li>loss of private keys</li>
                    <li>market volatility</li>
                    <li>liquidity constraints</li>
                  </ul>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    The issuer does not guarantee uninterrupted operation of blockchain networks, exchanges, or digital wallets used in connection with CGO Tokens.
                  </p>
                </div>

                {/* ── Cryptocurrency Market Volatility ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Cryptocurrency Market Volatility
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    The market value of CGO Tokens may fluctuate significantly over short periods of time. CGO Tokens represent digital units whose value is linked to the market value of physical gold. While gold has historically served as a store of value, the price of gold may fluctuate due to global economic conditions, interest rates, currency movements, geopolitical developments, and market demand. As a result, the value of CGO Tokens may rise or fall in line with the underlying gold price, and token holders may experience gains or losses.
                  </p>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    Although CGO Tokens are linked to the value of physical gold, their market price on digital asset exchanges may differ from the underlying gold value due to supply and demand dynamics, liquidity conditions, and trading activity.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Users should be aware that digital asset markets are highly volatile and speculative.
                  </p>
                </div>

                {/* ── Exchange Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Exchange Risk
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    CGO Tokens may be traded on third-party digital asset exchanges. The issuer does not operate, control, or guarantee the operations of any exchange platform.
                  </p>
                  <p className="fs-18 mb-1" style={{ color: t.textColor }}>Trading on digital asset exchanges involves risks including:</p>
                  <ul style={bulletListStyle} className="fs-18">
                    <li>exchange insolvency</li>
                    <li>cyberattacks or hacks</li>
                    <li>liquidity shortages</li>
                    <li>trading suspension</li>
                    <li>operational failures</li>
                  </ul>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Users assume full responsibility for selecting and interacting with any exchange platform.
                  </p>
                </div>

                {/* ── Smart Contract Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Smart Contract Risk
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    CGO Tokens are issued using blockchain-based smart contracts. While security measures and audits may be implemented, smart contracts are subject to potential coding vulnerabilities, software bugs, or exploits.
                  </p>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    Such vulnerabilities could result in loss of tokens, unintended transfers, or disruptions to token functionality.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Users acknowledge and accept these risks when interacting with blockchain technology.
                  </p>
                </div>

                {/* ── Custody Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Custody Risk
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    The physical gold supporting CGO Tokens is stored with professional vault and custody providers. However, custody arrangements may be exposed to risks including:
                  </p>
                  <ul style={bulletListStyle} className="fs-18">
                    <li>operational failures</li>
                    <li>security breaches</li>
                    <li>insolvency of service providers</li>
                    <li>force majeure events</li>
                  </ul>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    In addition, token holders managing their own digital wallets must safeguard private keys; loss or compromise of private keys may result in permanent loss of access to CGO Tokens.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    While safeguards and insurance may be in place, no custody system is completely risk-free.
                  </p>
                </div>

                {/* ── Regulatory Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Regulatory Risk
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    Digital asset regulations continue to evolve globally. Changes in laws or regulatory policies may impact the issuance, trading, custody, or redemption of CGO Tokens.
                  </p>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    Regulatory actions in certain jurisdictions may restrict or prohibit the use, ownership, or transfer of digital assets. The issuer does not guarantee that CGO Tokens will remain available in all jurisdictions.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Users are responsible for ensuring compliance with the laws and regulations applicable in their jurisdiction.
                  </p>
                </div>

                {/* ── No Guarantee of Liquidity ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    No Guarantee of Liquidity
                  </h5>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    The issuer does not guarantee that a secondary market for CGO Tokens will exist or continue to exist. The availability of buyers or sellers on exchanges may vary, and users may not be able to sell their tokens at desired prices or timeframes.
                  </p>
                </div>

                {/* ── Redemption Disclaimer ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Redemption Disclaimer
                  </h5>
                  <p className="fs-18 mb-1" style={{ color: t.textColor }}>
                    Redemption of CGO Tokens for physical gold may be subject to:
                  </p>
                  <ul style={bulletListStyle} className="fs-18">
                    <li>minimum redemption quantities</li>
                    <li>identity verification procedures</li>
                    <li>logistics and vault handling fees</li>
                    <li>processing times</li>
                    <li>regulatory compliance requirements</li>
                  </ul>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Redemption processes may change at the discretion of the issuer or due to regulatory or operational requirements.
                  </p>
                </div>

                {/* ── Jurisdictional Restrictions ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Jurisdictional Restrictions
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    CGO Tokens are not offered to individuals or entities located in jurisdictions where the purchase, holding, or trading of digital assets is prohibited or restricted by law.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Users are solely responsible for ensuring that their acquisition or use of CGO Tokens complies with applicable laws in their jurisdiction.
                  </p>
                </div>

                {/* ── Vault and Storage Provider Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Vault and Storage Provider Risk
                  </h5>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    The physical gold underlying CGO Tokens is stored with professional vaulting and custody providers. Although such providers implement high standards of security and insurance coverage, vault operators remain third-party entities and may be exposed to operational, financial, or security risks. Any disruption in vault operations could impact the accessibility, transfer, or verification of the underlying gold assets.
                  </p>
                </div>

                {/* ── Counterparty Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Counterparty Risk
                  </h5>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    The CGO ecosystem may involve multiple third-party service providers, including custodians, vault operators, auditors, exchanges, liquidity providers, and technology partners. Failure, insolvency, or operational disruptions involving any such counterparties may impact the functionality, liquidity, or settlement of CGO Tokens.
                  </p>
                </div>

                {/* ── Force Majeure and Market Disruption Risk ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Force Majeure and Market Disruption Risk
                  </h5>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    Extraordinary events such as geopolitical conflicts, financial market disruptions, government interventions, or force majeure circumstances may affect gold markets, custody arrangements, trading infrastructure, or blockchain networks. Such events may temporarily or permanently impact the issuance, redemption, or trading of CGO Tokens.
                  </p>
                </div>

                {/* ── Limitation of Liability ── */}
                <div style={cardStyle}>
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Limitation of Liability
                  </h5>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    To the fullest extent permitted by law, the issuer, its affiliates, partners, and service providers shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from the use of CGO Tokens or reliance on information provided on this website.
                  </p>
                </div>

                {/* ── Acceptance of Terms ── */}
                <div
                  style={{
                    backgroundColor: t.noteBg,
                    borderLeft: '4px solid #c9a84c',
                    borderRadius: '8px',
                    padding: '1.25rem 1.5rem',
                    marginTop: '1rem',
                    marginBottom: '2rem',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  <h5 style={{ color: t.titleColor, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
                    Acceptance of Terms
                  </h5>
                  <p className="fs-18 mb-2" style={{ color: t.textColor }}>
                    By accessing this website or acquiring CGO Tokens, users acknowledge that they have read, understood, and accepted the risks and disclaimers described above.
                  </p>
                  <p className="fs-18 mb-0" style={{ color: t.textColor }}>
                    <strong style={{ color: t.titleColor }}>If you do not agree with these terms, you should not acquire or use CGO Tokens.</strong>
                  </p>
                </div>

              </div>
            </div>
          </div>
        </section>

      </section>
    </div>
  );
};

export default Disclaimers;